import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { analytics } from '../../services/analytics';
import SEOPageLayout from '../../components/SEOPageLayout';

const PackagePricing = () => {
  useEffect(() => {
    analytics.track('Feature Page View', { feature: 'Package Pricing' });
  }, []);

  return (
    <SEOPageLayout
      title="Package Pricing Software - Bundle Services for Profit | QuoteTier"
      description="Simplify complex services into easy-to-buy packages. Create standard bundles for your services to speed up quoting and confuse clients less."
      canonical="/features/package-pricing-software"
      ogTitle="Sell Outcomes, Not Hours"
      ogDescription="Move away from itemized lists of parts and labor. Sell clear, value-based packages that clients understand and buy."
    >
      <section className="py-32 md:py-40 lg:py-48">
        <div className="container max-w-7xl mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="font-instrument text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-tight mb-6">
              Sell <span className="text-primary font-bold">Outcomes</span>, Not Hours
            </h1>
            <p className="font-jetbrains text-xl md:text-2xl text-gray-600 tracking-tight leading-relaxed mb-8">
              Clients don't want to buy 10 hours of labor and 50 parts. They want a "Kitchen Renovation Package". Give them what they want.
            </p>
            <div className="font-instrument mt-12 flex flex-col sm:flex-row gap-6 justify-center items-center mb-6">
              <Link to="/signup">
                <Button className="px-8 py-4 text-xl font-semibold bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg">
                  Create Your First Package
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">The Power of Bundling</h2>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            <Card className="border border-black">
              <CardHeader><CardTitle className="font-instrument text-xl">Simplified Decisions</CardTitle></CardHeader>
              <CardContent><p className="font-jetbrains text-sm text-gray-600">Decision fatigue kills sales. Bundling reduces 20 decisions down to 1 ("Do I want the Silver or Gold package?").</p></CardContent>
            </Card>
            <Card className="border border-black">
              <CardHeader><CardTitle className="font-instrument text-xl">Hidden Complexity</CardTitle></CardHeader>
              <CardContent><p className="font-jetbrains text-sm text-gray-600">You can still track all your costs internally, but present a clean, simple price to the client. No more haggling over line items.</p></CardContent>
            </Card>
            <Card className="border border-black">
              <CardHeader><CardTitle className="font-instrument text-xl">Faster Quoting</CardTitle></CardHeader>
              <CardContent><p className="font-jetbrains text-sm text-gray-600">Save your standard packages as templates. Quote a complex job in seconds by just selecting the "Premium Package" template.</p></CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-black text-white">
        <div className="container max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-6">Start Packaging for Profit</h2>
          <Link to="/signup">
            <Button className="px-10 py-4 font-instrument text-xl font-semibold bg-white text-black hover:bg-gray-200 rounded-lg shadow-lg">
              Start Free
            </Button>
          </Link>
        </div>
      </section>
    </SEOPageLayout>
  );
};

export default PackagePricing;
